import {
  type HtmlNormalizeOptions,
  normalizeCmsHtml,
  normalizeHtmlWhitespace,
} from '@/app/utils/html/html-normalize';
import { sanitizeHtml } from '@/app/utils/html/html-sanitize';

export type HtmlMarkup = {
  __html: string;
};

export { normalizeCmsHtml, normalizeHtmlWhitespace, sanitizeHtml };
export type { HtmlNormalizeOptions };

/**
 * Normalizes CMS HTML first, then sanitizes it for safe rendering.
 *
 * ```ts
 * import { sanitizeHTML } from '@/utils/format/html';
 *
 * sanitizeHTML('<br><ul><li><p>เอกสารประกอบ</p></li></ul>');
 * // '<ul><li>เอกสารประกอบ</li></ul>'
 * ```
 */
export const sanitizeHTML = (
  html: string,
  options: HtmlNormalizeOptions = {}
): string => {
  return sanitizeHtml(normalizeCmsHtml(html, options));
};

/**
 * Builds the object shape required by React `dangerouslySetInnerHTML`.
 * Use this helper instead of passing raw CMS HTML directly.
 *
 * ```tsx
 * import { buildHtmlMarkup } from '@/utils/format/html';
 *
 * <p dangerouslySetInnerHTML={buildHtmlMarkup('<span>สมัครสินเชื่อ</span>')} />
 * ```
 */
export const buildHtmlMarkup = (
  html: string,
  options: HtmlNormalizeOptions = {}
): HtmlMarkup => ({
  __html: sanitizeHTML(html, options),
});

/**
 * Builds `dangerouslySetInnerHTML` for `<script type="application/ld+json">`.
 * Do not use {@link buildHtmlMarkup} here: CMS HTML sanitization corrupts JSON.
 *
 * ```tsx
 * <script
 *   type="application/ld+json"
 *   dangerouslySetInnerHTML={buildJsonLdMarkup(schemaObject)}
 * />
 * ```
 */
export const buildJsonLdMarkup = (data: unknown): HtmlMarkup => ({
  __html: JSON.stringify(data).replace(/</g, '\\u003c'),
});
